import { SimulationDocumentDto } from 'api/documents';
import { BranchCode } from 'api/risk';
import { createStoreWithReducer, shallow } from 'shared/store';
import { DocumentProgressDto } from 'shared/signalR/hooks/useDocumentNotifications';
import { contractualizationReducer, initialState } from '../contexts/reducer';
import { ContractualizationAction } from '../contexts/actions/types';
import { ContractualizationState } from '../contexts/types';
import { QuoteContractualizationData } from '../types/contractualizationData';
import { fetchSimulationDocuments as fetchSimulationDocumentsAsync } from '../api/fetchSimulationDocuments';
import { sendContractSignatureRequest as sendContractSignatureRequestAsync } from '../api/sendContractSignatureRequest';

/**
 * Store pour la contractualization
 * Accessible partout dans l'application, meme hors des Providers (ex: Panels)
 *
 * Ce store contient:
 * - quoteData: Copie synchronisee depuis SubscriptionProcessContext
 *              (pour les Panels rendus hors du Provider via Portal)
 * - Les documents (fetches separement)
 * - Les etats des operations async
 * - Les erreurs
 *
 * La source de verite pour quoteData reste SubscriptionProcessContext.
 * Le store est synchronise automatiquement via useEffect dans ContractualizationStep.
 */
export const useContractualizationStore = createStoreWithReducer<
  ContractualizationState,
  ContractualizationAction,
  {
    // Async actions
    fetchSimulationDocuments: (
      quoteId: string,
      branchCode: BranchCode
    ) => Promise<void>;
    sendContractSignatureRequest: (params: {
      quoteId: string;
      fullName: string;
      email: string;
      hasGroupContract: boolean;
      bankAccountId?: string;
    }) => Promise<{ success: boolean }>;
    // Sync actions
    setQuoteData: (data: QuoteContractualizationData | null) => void;
    setDocuments: (documents: SimulationDocumentDto[]) => void;
    setError: (error: string | null) => void;
    setIsFetchingDocuments: (fetching: boolean) => void;
    setIsSendingContract: (sending: boolean) => void;
    updateDocumentFromNotification: (notification: DocumentProgressDto) => void;
    addContractDocument: (hasGroupContract: boolean) => void;
    setFetchedDocuments: (
      branchCode: BranchCode,
      documents: SimulationDocumentDto[]
    ) => void;
    reset: () => void;
  }
>(contractualizationReducer, initialState, (dispatch, getState) => {
  // Sync actions
  const syncActions = {
    setQuoteData: (data: QuoteContractualizationData | null) =>
      dispatch({ type: 'SET_QUOTE_DATA', payload: data }),

    setDocuments: (documents: SimulationDocumentDto[]) =>
      dispatch({ type: 'SET_DOCUMENTS', payload: documents }),

    setError: (error: string | null) =>
      dispatch({ type: 'SET_ERROR', payload: error }),

    setIsFetchingDocuments: (fetching: boolean) =>
      dispatch({ type: 'SET_IS_FETCHING_DOCUMENTS', payload: fetching }),

    setIsSendingContract: (sending: boolean) =>
      dispatch({ type: 'SET_IS_SENDING_CONTRACT', payload: sending }),

    updateDocumentFromNotification: (notification: DocumentProgressDto) =>
      dispatch({
        type: 'UPDATE_DOCUMENT_FROM_NOTIFICATION',
        payload: notification
      }),

    addContractDocument: (hasGroupContract: boolean) =>
      dispatch({
        type: 'ADD_CONTRACT_DOCUMENT',
        payload: { hasGroupContract }
      }),

    setFetchedDocuments: (
      branchCode: BranchCode,
      documents: SimulationDocumentDto[]
    ) =>
      dispatch({
        type: 'SET_FETCHED_DOCUMENTS',
        payload: { branchCode, documents }
      }),

    reset: () => dispatch({ type: 'RESET' })
  };

  // Async actions
  return {
    ...syncActions,

    fetchSimulationDocuments: (quoteId: string, branchCode: BranchCode) =>
      fetchSimulationDocumentsAsync(quoteId, branchCode, syncActions),

    sendContractSignatureRequest: (params: {
      quoteId: string;
      fullName: string;
      email: string;
      hasGroupContract: boolean;
      bankAccountId?: string;
    }) => sendContractSignatureRequestAsync(params, syncActions)
  };
});

// ============================================
// Hooks de selection (avec shallow compare)
// ============================================

/**
 * Selectionne les donnees quote depuis le store
 * Utilise par useQuoteContractualizationData comme fallback pour les Panels
 */
export const useQuoteDataFromStore = () =>
  useContractualizationStore(state => state.quoteData);

/**
 * Selectionne les documents
 */
export const useDocuments = () =>
  useContractualizationStore(state => state.documents, shallow);

/**
 * Selectionne l'etat de recuperation des documents
 */
export const useIsFetchingDocuments = () =>
  useContractualizationStore(state => state.isFetchingDocuments);

/**
 * Selectionne l'etat d'envoi du contrat
 */
export const useIsSendingContract = () =>
  useContractualizationStore(state => state.isSendingContract);

/**
 * Selectionne l'erreur
 */
export const useError = () =>
  useContractualizationStore(state => state.error);

/**
 * Retourne toutes les actions du store
 */
export const useContractualizationActions = () =>
  useContractualizationStore.actions;

/**
 * Acces direct au store (hors composants React)
 */
export const contractualizationStore = {
  getState: useContractualizationStore.getState,
  setState: useContractualizationStore.setState,
  dispatch: useContractualizationStore.dispatch,
  actions: useContractualizationStore.actions,
  subscribe: useContractualizationStore.subscribe
};
